import { supabase } from '../supabase/client';
import type { RaumBlock, SchluesselZeile, ZaehlerEintrag } from './typen';

export interface KatalogEintrag {
  id: string;
  raum: string;
  pruefpunkte: string[];
  sortierung: number;
  aktiv: boolean;
}

export const SCHLUESSEL_STANDARD: SchluesselZeile[] = [
  'Haustür',
  'Wohnungstür',
  'Keller',
  'Briefkasten',
  'Garage',
  'Sonstige',
].map(bezeichnung => ({ bezeichnung, anzahl: 0 }));

export async function ladeKatalog(): Promise<KatalogEintrag[]> {
  const { data, error } = await supabase
    .from('uebergabe_katalog')
    .select('*')
    .eq('aktiv', true)
    .order('sortierung');
  if (error) throw error;
  return (data ?? []) as KatalogEintrag[];
}

/**
 * Steckt in diesem Raumblock bereits erfasste Arbeit? Grundlage für die Rückfrage vor
 * dem Entfernen eines Raums (Fund W4): ein zweiter Tipp auf einen Raum-Chip löschte den
 * Raum samt Prüfpunkten, Notizen, Fotoverweisen und den dort erfassten Zählern — ohne
 * Rückfrage, und auf dem Tablet mit 13 Chips nebeneinander ist der Fehlgriff
 * wahrscheinlich. Ein frisch gewählter, noch unberührter Raum soll dagegen ohne
 * Rückfrage wieder abwählbar bleiben.
 *
 * Praxis-Ergänzung: zählen jetzt auch die Raum-Fotos (unabhängig von Prüfpunkt-Mängeln)
 * und eine eingetragene Bezeichnung bei „Sonstiger Raum" als erfasste Arbeit — beides
 * ginge beim Entfernen sonst ohne Rückfrage verloren.
 */
export function raumHatErfassteDaten(block: RaumBlock, zaehler: ZaehlerEintrag[] = []): boolean {
  const pruefpunkte = block.pruefpunkte ?? [];
  if (pruefpunkte.some(p => p.status !== null || p.notiz?.trim() || p.fotos?.length)) return true;
  if ((block.fotos ?? []).length) return true;
  if ((block.bezeichnung ?? '').trim()) return true;
  const ids = block.zaehler_ids ?? [];
  if (!ids.length) return false;
  // Ein Zähler-Eintrag entsteht erst durch eine Handlung im Raum (Stand, Foto oder
  // bewusstes Hinzufügen) — schon seine Existenz ist erfasste Arbeit.
  return zaehler.some(z => ids.includes(z.id));
}

/**
 * Eindeutige Identität eines Raumblocks für Zuordnung/Entfernen/Ändern
 * (SchrittRundgang.tsx) — die `kennung`, falls vorhanden, sonst `raum` als Ersatz für
 * ältere Blöcke ohne Kennung (dort war `raum` ohnehin eindeutig, s. Kommentar am Feld
 * in lib/uebergabe/typen.ts).
 */
export function raumKennung(block: RaumBlock): string {
  return block.kennung ?? block.raum;
}

/**
 * Der Name, unter dem dieser Raum in Oberfläche und PDF erscheint: die frei eingetragene
 * `bezeichnung` (nur bei „Sonstiger Raum" belegbar), sonst der Katalog-Raumname. Dieselbe
 * Regel steht ein zweites Mal in supabase/functions/uebergabe-finalisieren/index.ts (Edge
 * Functions können lib/ nicht importieren, s. Kommentar dort).
 */
export function effektiverRaumName(block: RaumBlock): string {
  return (block.bezeichnung ?? '').trim() || block.raum;
}

/**
 * Hängt an jeden Namen ab seinem ZWEITEN Auftreten in `namen` eine laufende Nummer —
 * „Sonstiger Raum (2)", „Sonstiger Raum (3)" usw. — damit zwei gleichnamige Raumblöcke
 * (z. B. zwei „Sonstiger Raum" ohne eigene Bezeichnung, s. baueRaumBloecke()) in der
 * Rundgang-Ansicht und im PDF unterscheidbar bleiben. Der erste Treffer bleibt
 * unverändert, nur jeder WEITERE bekommt die Nummer. Rein anzeigebezogen: die
 * zurückgegebenen Namen dürfen NIRGENDS als fachlicher Wert weiterverwendet werden (z. B.
 * nicht als `raum`-Feld eines ZaehlerEintrag oder für den Zähler-Standort-Abgleich in
 * zaehlerZuordnung.ts) — dieselbe Zuordnung steht dupliziert in
 * supabase/functions/uebergabe-finalisieren/index.ts (Edge Functions können lib/ nicht
 * importieren, s. Kommentar dort zu effektiverRaumName).
 */
export function nummeriereGleicheNamen(namen: string[]): string[] {
  const gesehen = new Map<string, number>();
  return namen.map(name => {
    const anzahl = (gesehen.get(name) ?? 0) + 1;
    gesehen.set(name, anzahl);
    return anzahl === 1 ? name : `${name} (${anzahl})`;
  });
}

// Reihenfolge der Auswahl gewinnt: der Rundgang läuft so, wie der Mitarbeiter
// durch die Wohnung geht, nicht wie der Katalog sortiert ist.
//
// Jeder Block bekommt eine eigene `kennung` (crypto.randomUUID()) — Grundlage dafür,
// dass „Sonstiger Raum" mehrfach nebeneinander angelegt werden kann, ohne dass ein
// zweiter Aufruf den ersten überschreibt (vorher war `raum` selbst der Schlüssel).
export function baueRaumBloecke(
  auswahl: string[],
  katalog: KatalogEintrag[]
): RaumBlock[] {
  return auswahl.flatMap(raum => {
    const eintrag = katalog.find(k => k.raum === raum);
    if (!eintrag) return [];
    return [
      {
        kennung: crypto.randomUUID(),
        raum,
        bezeichnung: null,
        fotos: [],
        zaehler_ids: [],
        pruefpunkte: eintrag.pruefpunkte.map(name => ({
          name,
          status: null,
          notiz: '',
          fotos: [],
        })),
      },
    ];
  });
}
